import { Navigate, Outlet, useLocation } from 'react-router';
import { ShieldOff } from 'lucide-react';
import { landingRouteFor, useAuthStore } from '@/store/authStore';
import { EmptyState } from '@/components/ui/Feedback';
import { LinkButton } from '@/components/ui/Button';
import type { Permission } from '@/types';

/** Blocks unauthenticated access and remembers where the user was heading. */
export function RequireAuth() {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * Route-level authorisation, by permission.
 *
 * This mirrors the `@PreAuthorize` on the matching controller — the API enforces
 * the same rule, so a user who edits the URL gets a 403 rather than data. Gating
 * on a capability rather than a role code is what lets a workspace invent a role
 * and have it work here without a release.
 */
export function RequirePermission({
  permissions,
  /** When true the user needs every permission listed, not just one. */
  requireAll = false,
}: {
  permissions: Permission[];
  requireAll?: boolean;
}) {
  const user = useAuthStore((state) => state.user);
  const can = useAuthStore((state) => state.can);
  const canAny = useAuthStore((state) => state.canAny);

  if (!user) return <Navigate to="/login" replace />;

  const allowed = requireAll ? can(...permissions) : canAny(...permissions);
  if (!allowed) {
    return (
      <EmptyState
        icon={<ShieldOff aria-hidden />}
        title="You don't have access to this page"
        description="Your role does not include this permission. Ask an administrator to grant it, or head back to your workspace."
        action={
          <LinkButton to={landingRouteFor(user)} variant="secondary">
            Back to my workspace
          </LinkButton>
        }
        className="card"
      />
    );
  }
  return <Outlet />;
}

/** Signed-in users should never see the login screen again. */
export function RedirectIfAuthenticated() {
  const user = useAuthStore((state) => state.user);
  return user ? <Navigate to={landingRouteFor(user)} replace /> : <Outlet />;
}

/** `/m` picks the right scanner app for the signed-in role's persona. */
export function MobileEntry() {
  const user = useAuthStore((state) => state.user);
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.mobilePersona === 'DISPATCH' ? '/m/dispatch' : '/m/receive'} replace />;
}
